/**
 * Selectors for order close confirmation state
 */
import type { PendingCloseConfirmation } from '../../reducers/ui/orderCloseConfirmation';

/**
 * Qué notas del aviso puede enseñar una pantalla:
 * - `any`: pantallas generales (tabla de validación, Gestión Horaria) → la
 *   más reciente, sea de la orden que sea;
 * - `order`: detalle de una OT, campaña o recorrido → solo la de ESA orden.
 *   `orderId` sin cargar todavía = ninguna.
 */
export type CloseOrderConfirmationScope =
  | { kind: 'any' }
  | { kind: 'order'; orderId: string | undefined };

/**
 * All pending notes, one per order, most recent last
 */
export const getPendingCloseConfirmations = (
  state: any
): PendingCloseConfirmation[] =>
  state.orderCloseConfirmation?.pendingConfirmations ?? [];

/**
 * La nota que toca enseñar en una pantalla. En el detalle de una orden, una
 * nota de OTRA orden espera a que llegues a la suya o a una pantalla general:
 * antes salía en la primera pantalla con aviso, encima de cualquier orden
 * (beta 2026-10-12: el de Ot.0000015 sobre Ot.0000297, el de Su.0000379 sobre
 * Su.0000412).
 */
export function pickPendingCloseConfirmation(
  pending: PendingCloseConfirmation[],
  scope: CloseOrderConfirmationScope
): PendingCloseConfirmation | undefined {
  if (scope.kind === 'order') {
    return scope.orderId
      ? pending.find((p) => p.parentOrderId === scope.orderId)
      : undefined;
  }
  return pending[pending.length - 1];
}

/**
 * Get the list of orders that were declined (user said "No, keep open")
 */
export const getDeclinedOrders = (state: any) =>
  state.orderCloseConfirmation?.declinedOrders || [];

/**
 * Check if a specific order was declined
 */
export const isOrderDeclined = (orderId: string) => (state: any) =>
  state.orderCloseConfirmation?.declinedOrders?.includes(orderId) || false;
